/**
 * MainTabs — renders the hierarchical tab navigation for the project workspace.
 *
 * Recursively renders nested tab levels. At each level, the active tab's children
 * (if any) are rendered as the next tab bar below. When a leaf tab (no children)
 * is reached, the `children` prop (the actual screen content) is rendered.
 *
 * This creates the multi-level navigation: e.g., "Analysis" -> "Balances and Activities" -> "Aging"
 */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useSelector, useDispatch } from "react-redux";
import { setActiveTab } from "Redux/nav/actions";
import { useHelpPanel } from "Components/HelpPanel";

// No tabs are disabled — all screens are always navigable.
// Individual screens handle their own "no master" messaging.

// Entry point — reads mainTabs from Redux nav state and delegates to the recursive MainTabs component
const Index = ({ children }) => {
  const dispatch = useDispatch();
  const nav = useSelector((state) => state.nav);
  const { mainTabs } = nav;

  const setTab = (tab, ancestor) => {
    dispatch(setActiveTab(tab, ancestor));
  };

  return (
    <MainTabs
      tabs={mainTabs}
      setTab={setTab}
      ancestor="root"
      children={children}
    />
  );
};
export default Index;

// -------------------------------------------------------------
// -------------------------------------------------------------
// Recursive tab renderer — renders one level of tabs, then either recurses into
// the active tab's sub-tabs or renders the page content (children) at the leaf level.
const MainTabs = ({ tabs, ancestor, setTab, depth, children }) => {
  const pickedTab = tabs.find((tab) => tab.active);
  const isLeaf = !pickedTab.tabs;
  const { toggle, hasContent } = useHelpPanel();

  return (
    <div className="box__">
      <div className="tabs" style={{ display: "flex", alignItems: "center" }}>
        <ul style={{ flex: 1 }}>
          {tabs.map((tab) => {
            if (!tab.invisible) {
              return (
                <Tab
                  tab={tab}
                  key={tab.tab}
                  setTab={setTab}
                  isActive={tab.active}
                  depth={depth}
                  ancestor={ancestor}
                />
              );
            }
            return null;
          })}
        </ul>
        {isLeaf && hasContent && (
          <button
            onClick={toggle}
            title="Help"
            style={{
              background: "linear-gradient(135deg, #430098 0%, #79589F 100%)",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.85rem",
              marginRight: "0.75rem",
              flexShrink: 0,
              boxShadow: "0 2px 10px rgba(67, 0, 152, 0.3)",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(67, 0, 152, 0.45)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 10px rgba(67, 0, 152, 0.3)";
            }}
          >
            <i className="fa-solid fa-question" style={{ fontSize: "0.95rem" }} />
          </button>
        )}
      </div>
      {pickedTab.tabs ? (
        <MainTabs
          tabs={pickedTab.tabs}
          setTab={setTab}
          depth={depth + 1}
          ancestor={pickedTab.tab}
          children={children}
        />
      ) : (
        children
      )}
    </div>
  );
};

// -------------------------------------------------------------
// -------------------------------------------------------------
// Individual tab button — renders icon + label, triggers navigation on click
const Tab = ({ tab, setTab, ancestor, isActive }) => {
  return (
    <li className={isActive ? "is-active" : ""}>
      <a
        role="button"
        tabIndex={0}
        onClick={() => {
          setTab(tab.tab, ancestor);
        }}
      >
        <span className="icon is-small">
          <i className={tab.iconclass} aria-hidden="true" />
        </span>
        <span>{tab.headline}</span>
      </a>
    </li>
  );
};
